import { Group, Stack, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/browser';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { resetPassword } from '../../../api/auth-client/auth-client';
import { checkValidEmail } from '../../../helpers';
import { useDocumentTitle } from '../../../helpers/general-helpers';
import { baseWhite, inkLight, secondaryBase } from '../../../styles/design-tokens';
import { Button } from '../../../ui-library/button/button';
import { Divider } from '../../../ui-library/divider/divider';
import { Icon } from '../../../ui-library/icon/icon';
import { H3, Text } from '../../../ui-library/typography/typography';
import { Layout } from '../layout';

export const ResetPasswordForm = () => {
  useDocumentTitle('Reset Password - Bloomfilter');

  const i18n = useTranslation();
  const navigate = useNavigate();

  const form = useForm({
    initialValues: {
      email: '',
    },
    validate: {
      email: (value) => (checkValidEmail(value) ? null : 'Please provide a valid email.'),
    },
  });

  const resetPasswordMutation = useMutation({
    mutationKey: ['reset-password', form.values.email] as const,
    mutationFn: async (email: string) => {
      if (!email) {
        throw new Error('Email is required!');
      }
      return resetPassword(email);
    },
    onSuccess: () => {
      navigate('/reset-password-success');
    },
    onError: () => {
      navigate('/reset-password-success');
    },
  });

  const handleSubmit = async () => {
    try {
      await resetPasswordMutation.mutateAsync(form.values.email);
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  return (
    <Layout>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap={32}>
          <Group gap={16}>
            <Icon name="key" size={32} />
            <H3>{i18n.t('common.form.forgot_password')}</H3>
          </Group>
          <Text>
            {i18n.t('common.form.reset_help')}
            <br />
            {i18n.t('common.form.enter_email')}
          </Text>
          <Stack gap={24} role="form" aria-label={i18n.t('common.form.forgot_password_form')}>
            <Divider />
            <TextInput
              label={i18n.t('common.form.email')}
              placeholder={i18n.t('common.placeholders.email')}
              value={form.values.email}
              onChange={(event) => form.setFieldValue('email', event.currentTarget.value.trim())}
              error={form.errors.email}
              w="100%"
              autoComplete="email"
              required
            />
          </Stack>
          <Divider />
          <Stack gap={24} align="center">
            <Button
              type="submit"
              loaderProps={{ size: 'xs', color: secondaryBase }}
              w="100%"
              radius="xl"
              rightSection={<Icon name="arrow_forward" size={18} color={baseWhite} />}
            >
              {i18n.t('common.form.reset_password')}
            </Button>
            <Text size="small">{i18n.t('common.form.remember_password')}</Text>
            <Button
              variant="outline"
              onClick={() => navigate('/')}
              w="100%"
              radius="xl"
              rightSection={<Icon name="login" size={18} color={inkLight} />}
            >
              {i18n.t('common.form.login_to_bloomfilter')}
            </Button>
          </Stack>
        </Stack>
      </form>
    </Layout>
  );
};
